/**
 * Centralized Permissions Configuration
 * Single Source of Truth for all permission-related constants
 *
 * Usage:
 * - Import `Permissions` enum for type-safe permission checks
 * - Import `PermissionValues` for runtime permission arrays
 * - Import `PermissionType` for TypeScript type annotations
 */

/**
 * All available permissions in the system
 * Use this enum in route definitions and permission checks
 */
export enum Permissions {
  // Admin permission (full access)
  ADMIN = "ADMIN",

  // Report read permissions
  ADS_PERFORMANCE = "report:read:ads-performance",
  ADS_SUMMARY = "report:read:ads-summary",
  BRAND_PROTECTION = "report:read:brand-protection",
  RETURNS = "report:read:returns",
  REVIEW_REPORT = "report:read:reviews-report",
  SALES_REPORT = "report:read:sales-report",
  ADS_CAMPAIGN = "report:read:campaigns-report",
  INVENTORY = "report:read:inventory-report",
  ALERTS = "report:read:product-alerts",
  PERFORMANCE = "report:read:product-performance",
  ACCOUNT_HEALTH = "report:read:account-health",
  WAREHOUSE = "report:read:warehouse",
  CUSTOMER_ANALYTICS = "report:read:customer-analytics",
  ZINUS_CALCULATOR = "report:read:zinus",
  FUNNEL = "report:read:search-query-performance",
  PRODUCT_REVIEWS = "report:read:product-reviews",
  EMAILS = "report:read:email-management",
  ADVERTISING_ANALYTICS = "report:read:advertising-analytics",
  PROFIT_ANALYSIS = "report:read:profit-analysis",
  COMPETITOR = "report:read:competitor-report",
  CATEGORY_ANALYSIS = "report:read:category-reporting",

  // Report edit permissions
  EDIT_ADS_CAMPAIGN = "report:edit:campaigns-report",
}

/**
 * TypeScript type for permission values
 * Use for type annotations where string permission is expected
 */
export type PermissionType = `${Permissions}`;

/**
 * Array of all permission values for runtime checks
 */
export const PermissionValues = Object.values(Permissions) as PermissionType[];

/**
 * Permission groups for common use cases
 */
export const PermissionGroups = {
  /** Permissions required for Insightful dashboard */
  INSIGHTFUL: [
    Permissions.SALES_REPORT,
    Permissions.ADS_SUMMARY,
    Permissions.REVIEW_REPORT,
    Permissions.BRAND_PROTECTION,
  ],

  /** All advertising-related permissions */
  ADVERTISING: [
    Permissions.FUNNEL,
    Permissions.ADS_SUMMARY,
    Permissions.ADVERTISING_ANALYTICS,
    Permissions.ADS_CAMPAIGN,
  ],

  CATEGORY: [
    Permissions.CATEGORY_ANALYSIS,
    Permissions.COMPETITOR,
  ],

  PRODUCT_REVIEWS: [
    Permissions.REVIEW_REPORT,
    Permissions.PRODUCT_REVIEWS,
  ],

  ACCOUNT_HEALTH: [
    Permissions.ACCOUNT_HEALTH,
    Permissions.ALERTS,
    Permissions.BRAND_PROTECTION,
  ],

  ADMIN: [
    Permissions.ADMIN,
    Permissions.EMAILS,
  ],

  /** All read permissions */
  ALL_READ: PermissionValues.filter((p) => p.startsWith("report:read:")),

  /** All edit permissions */
  ALL_EDIT: PermissionValues.filter((p) => p.startsWith("report:edit:")),
} as const;

/**
 * Map permission to route path for redirects
 */
export const PermissionToRoute: Record<Permissions, string> = {
  [Permissions.ADMIN]: "/insightful",
  [Permissions.SALES_REPORT]: "/sales-and-traffic",
  [Permissions.RETURNS]: "/product-returns",
  [Permissions.ADS_SUMMARY]: "/advertising-summary",
  [Permissions.REVIEW_REPORT]: "/reviews",
  [Permissions.BRAND_PROTECTION]: "/brand-protection",
  [Permissions.ADS_CAMPAIGN]: "/campaigns",
  [Permissions.INVENTORY]: "/inventory",
  [Permissions.ALERTS]: "/product-alerts",
  [Permissions.PERFORMANCE]: "/product-performance",
  [Permissions.ACCOUNT_HEALTH]: "/account-health",
  [Permissions.WAREHOUSE]: "/warehouse",
  [Permissions.CUSTOMER_ANALYTICS]: "/customer-analytics",
  [Permissions.ZINUS_CALCULATOR]: "/zinus-calculator",
  [Permissions.FUNNEL]: "/query",
  [Permissions.PRODUCT_REVIEWS]: "/product-reviews",
  [Permissions.EMAILS]: "/communication",
  [Permissions.ADVERTISING_ANALYTICS]: "/ads-analytics",
  [Permissions.PROFIT_ANALYSIS]: "/contribution-margin",
  [Permissions.COMPETITOR]: "/competitor",
  [Permissions.CATEGORY_ANALYSIS]: "/category",
  [Permissions.ADS_PERFORMANCE]: "/advertising-summary",
  [Permissions.EDIT_ADS_CAMPAIGN]: "/campaigns",
};

/**
 * Get human-readable name for permission
 */
export function getPermissionDisplayName(permission: Permissions): string {
  const names: Record<Permissions, string> = {
    [Permissions.ADMIN]: "Administrator",
    [Permissions.SALES_REPORT]: "Sales Report",
    [Permissions.RETURNS]: "Product Returns",
    [Permissions.ADS_SUMMARY]: "Advertising Summary",
    [Permissions.ADS_PERFORMANCE]: "Advertising Performance",
    [Permissions.REVIEW_REPORT]: "Reviews Report",
    [Permissions.BRAND_PROTECTION]: "Brand Protection",
    [Permissions.ADS_CAMPAIGN]: "Campaigns",
    [Permissions.EDIT_ADS_CAMPAIGN]: "Edit Campaigns",
    [Permissions.INVENTORY]: "Inventory",
    [Permissions.ALERTS]: "Product Alerts",
    [Permissions.PERFORMANCE]: "Product Performance",
    [Permissions.ACCOUNT_HEALTH]: "Account Health",
    [Permissions.WAREHOUSE]: "Warehouse",
    [Permissions.CUSTOMER_ANALYTICS]: "Customer Analytics",
    [Permissions.ZINUS_CALCULATOR]: "Zinus Calculator",
    [Permissions.FUNNEL]: "Search Query Performance",
    [Permissions.PRODUCT_REVIEWS]: "Product Reviews",
    [Permissions.EMAILS]: "Communication",
    [Permissions.ADVERTISING_ANALYTICS]: "Advertising Analytics",
    [Permissions.PROFIT_ANALYSIS]: "Contribution Margin",
    [Permissions.COMPETITOR]: "Competitor Analysis",
    [Permissions.CATEGORY_ANALYSIS]: "Category Analysis",
  };

  return names[permission] || permission;
}
